// Hideo - the only names the app puts on window, set once by src/main.ts while the app loads (types in src/env.d.ts).
// The app's own code never reads them: every module imports what it uses.
// - Inline handlers (on…="" in index.html or in HTML strings): none calls an app function any more (they are delegated
//   listeners); if one ever must, its name goes in HANDLERS and `npm run check` accepts it (tools/check.py).
// - The development tools (tools/smoke-test.js, tools/screenshots.js) drive the app in the page through TOOLS, under the
//   names they have always used, so they work on older builds too.
import { I18N } from './i18n'
import { app } from './features/app'
import { usePickerStore } from './stores/picker'
import { useVideoStore, type VideoTab } from './stores/video'
import { useEditorStore, type EditorMode } from './stores/editor'
import { useDialogsStore } from './stores/dialogs'
import { useCloudStore } from './stores/cloud'
import { useCreatorsStore } from './stores/creators'
import { useTourStore } from './stores/tour'
import type { VideoPrefill } from './features/video'

/** Functions called by inline handlers */
export const HANDLERS = {}

/** What the development tools call (each store reached at call time) */
export const TOOLS = {
  I18N,
  pickProfile: (id: string) => usePickerStore().pick(id),
  switchTab: (page: string, replace?: boolean) => app.switchTab(page, replace),
  openVideoModal: (id?: string | null, prefill?: VideoPrefill) => useVideoStore().open(id || null, prefill),
  setVideoTab: (tab: VideoTab) => useVideoStore().setTab(tab),
  closeVideoModal: () => useVideoStore().close(),
  openScriptEditor: (id: string) => useEditorStore().openEditor(id),
  setScriptMode: (m: EditorMode, silent?: boolean) => useEditorStore().setMode(m, silent),
  closeScriptEditor: () => useEditorStore().closeEditor(),
  openSearch: () => useDialogsStore().show('search', {}),
  closeSearch: () => useDialogsStore().hide('search'),
  openProfiles: () => useCloudStore().openProfiles(),
  closeProfiles: () => useCloudStore().closeProfiles(),
  openCreatorDetailModal: (id: string) => useCreatorsStore().openDetail(id),
  closeCreatorDetailModal: () => useCreatorsStore().closeDetail(),
  openSourceModal: (id?: string | null) => useDialogsStore().show('source', { id: id || null, prefill: undefined }),
  closeSourceEditModal: () => useDialogsStore().hide('source'),
  openSavedModal: (id?: string | null) => useDialogsStore().show('saved', { id: id || null }),
  closeSavedModal: () => useDialogsStore().hide('saved'),
  openVaultModal: (id?: string | null) => useDialogsStore().show('hook', { id: id || null, prefill: undefined }),
  closeVaultModal: () => useDialogsStore().hide('hook'),
  openChangelog: () => useDialogsStore().show('changelog', {}),
  startTour: () => useTourStore().start(),
  endTour: (goHome?: boolean) => useTourStore().end(goHome)
}

/** Puts HANDLERS and TOOLS on window, plus `curTab` (the page shown, read live) */
export function installGlobals(win: Window = window): void {
  Object.assign(win, HANDLERS, TOOLS)
  Object.defineProperty(win, 'curTab', { get: () => app.page, configurable: true })
}
